import React, { useCallback, useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { MaskedLines, Reveal, EASE } from './reveal';

/**
 * HammerGallery — masonry-style photo grid + lightbox for the hammer landing.
 * Click any tile to open the lightbox; arrow keys and swipe-friendly buttons
 * navigate between shots. Body scroll is locked while the lightbox is open.
 */
const IMAGES = [
  { src: '/assets/gallery_row_indoor.webp',  alt: 'Four Absolute Hammers lined up in the shop', span: 'md:col-span-2 md:row-span-2' },
  { src: '/assets/gallery_fleet_sunset.webp', alt: 'Fleet of four hammers on a job site at sunset with excavator', span: '' },
  { src: '/assets/gallery_row_angle.webp',   alt: 'Row of hammers at an angle showing the pneumatic hardware', span: '' },
  { src: '/assets/gallery_solo_studio.webp',  alt: 'Studio product shot of a single Absolute Hammer with safety labels', span: '' },
  { src: '/assets/gallery_solo_sunset.webp', alt: 'Single hammer standing on a wet dock at sunset with cranes behind', span: 'md:col-span-2' },
];

export default function HammerGallery() {
  const [openIndex, setOpenIndex] = useState(null);

  const close = useCallback(() => setOpenIndex(null), []);
  const next = useCallback(
    () => setOpenIndex((i) => (i === null ? null : (i + 1) % IMAGES.length)),
    []
  );
  const prev = useCallback(
    () => setOpenIndex((i) => (i === null ? null : (i - 1 + IMAGES.length) % IMAGES.length)),
    []
  );

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [openIndex, close, next, prev]);

  return (
    <section
      id="hammer-gallery"
      data-testid="hammer-gallery-section"
      className="relative bg-[#050608] border-t border-white/5 py-16 sm:py-20 lg:py-28 overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <span className="h-px w-8 sm:w-10 bg-cyan-400" />
              <span className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.28em]">
                Photo Gallery
              </span>
            </div>
          </Reveal>
          <h2 className="font-display text-white text-3xl sm:text-4xl md:text-5xl font-bold uppercase leading-[1.02] tracking-tight">
            <MaskedLines
              delay={0.15}
              lines={[
                'Built, branded,',
                <span className="text-cyan-300">ready to work.</span>,
              ]}
            />
          </h2>
        </div>

        <div className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 auto-rows-[180px] sm:auto-rows-[220px] md:auto-rows-[210px] lg:auto-rows-[240px]">
          {IMAGES.map((img, i) => (
            <motion.button
              key={img.src}
              type="button"
              onClick={() => setOpenIndex(i)}
              data-testid={`hammer-gallery-tile-${i}`}
              initial={{ opacity: 0, y: 26, scale: 0.97 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
              className={`group relative overflow-hidden rounded-xl border border-white/10 bg-black focus:outline-none focus:ring-2 focus:ring-cyan-400/60 ${img.span}`}
              aria-label={`Open image: ${img.alt}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-70 group-hover:opacity-30 transition-opacity" />
            </motion.button>
          ))}
        </div>
      </div>

      {openIndex !== null && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="hammer-gallery-lightbox"
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={close}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); close(); }}
            aria-label="Close"
            data-testid="hammer-gallery-close"
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous"
            data-testid="hammer-gallery-prev"
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next"
            data-testid="hammer-gallery-next"
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <img
            src={IMAGES[openIndex].src}
            alt={IMAGES[openIndex].alt}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-tech text-white/70 text-[11px] tracking-[0.24em] uppercase">
            {openIndex + 1} / {IMAGES.length}
          </div>
        </div>
      )}
    </section>
  );
}
